/**
 * Phase 2R-MythologyOps — Cultural Observability Layer.
 *
 * NOT analytics.  NOT engagement dashboards.  NOT growth KPIs.
 *
 * This is the editorial conscience of ArenaX — a quiet weather-map for
 * where mythology is actually forming, city by city, sport by sport.  Used
 * at 9pm after a long week to decide whether `streak_risk_aftermath` is
 * earned in a given cluster.
 *
 * CEO contract:
 *   - cultural language only ("forming", "holding", "thickening", "earned",
 *     "stable", "emerging").  NEVER "high performing" / "top cluster".
 *   - restrained UI — no neon, no growth-gamification, no system-healthy energy.
 *   - quiet dots: gray (immature/emerging) / amber (forming/thickening) /
 *     emerald (earned/stable).
 *   - emerald state must feel earned, not celebratory.  Subtle emotional
 *     gravity, not flash.
 *   - empty state communicates INCUBATION, not failure.
 *   - hover reveals confidence; never explodes.
 *   - radial glows behave like ambient civic heat, not dashboard accents.
 *   - preserves `first_threshold_crossed_at` as the birth moment of
 *     mythology density.
 *   - super_admin only.
 */
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useArenaxTheme, ArenaxThemeToggle } from './useArenaxTheme';

const POLL_MS = 5 * 60 * 1000; // 5 minutes — mythology changes slowly

function StatusDot({ color }) {
  const palette = {
    emerald: '#10b981',
    amber: '#f59e0b',
    gray: '#64748b',
  };
  const c = palette[color] || palette.gray;
  return (
    <span
      className="inline-block w-1.5 h-1.5 rounded-full"
      style={{
        background: c,
        // Subtle emotional gravity on emerald — earned, not celebratory.
        boxShadow: color === 'emerald' ? `0 0 8px ${c}80` : 'none',
      }}
    />
  );
}

function StageBar({ stages }) {
  // Five thin vertical bars representing the 1→5 lifecycle stage distribution.
  const max = Math.max(1, ...Object.values(stages || {}));
  const order = ['1', '2', '3', '4', '5'];
  const colors = ['#475569', '#64748b', '#0891b2', '#0e7490', '#10b981'];
  return (
    <div className="flex items-end gap-1 h-6" data-testid="arenax-mythology-stages">
      {order.map((k, i) => {
        const v = stages?.[k] || 0;
        const h = v === 0 ? 2 : Math.max(2, (v / max) * 24);
        return (
          <span
            key={k}
            title={`Stage ${k}: ${v}`}
            className="w-1 rounded-sm"
            style={{ height: `${h}px`, background: colors[i], opacity: v === 0 ? 0.25 : 0.85 }}
          />
        );
      })}
    </div>
  );
}

function _formatBirthDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return null;
  }
}

function ClusterTile({ cluster }) {
  const pct = Math.round((cluster.completion_rate || 0) * 100);
  const born = _formatBirthDate(cluster.first_threshold_crossed_at);
  const isEarned = cluster.color === 'emerald';
  const isForming = cluster.color === 'amber';
  return (
    <div
      className="arenax-mythology-tile p-4"
      style={{
        background: 'var(--ax-mythology-tile-bg)',
        border: `1px solid ${
          isEarned
            ? 'rgba(16,185,129,0.35)'
            : isForming
            ? 'rgba(245,158,11,0.30)'
            : 'var(--ax-border-quiet)'
        }`,
        borderRadius: '4px',
        boxShadow: isEarned
          // Quiet earned-glow: ambient, never flashy.  Stronger than `none`,
          // softer than any spectator hype the rest of ArenaX uses.
          ? `var(--ax-mythology-tile-shadow), inset 0 0 24px rgba(16,185,129,0.05)`
          : 'var(--ax-mythology-tile-shadow)',
        // Mature clusters carry slightly more visual weight than emerging
        // ones — density hierarchy emerges WITHOUT screaming.
        opacity: cluster.color === 'gray' && pct < 20 ? 0.78 : 1,
        transition: 'border-color 600ms ease, box-shadow 600ms ease, opacity 600ms ease',
      }}
      data-testid={`arenax-mythology-tile-${cluster.city}-${cluster.sport_norm}`}
    >
      <div className="flex items-baseline justify-between">
        <div className="space-y-0.5 min-w-0 flex-1">
          <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500 truncate">
            {cluster.city} · {cluster.sport}
          </div>
          <div className="flex items-center gap-2 mt-1">
            <StatusDot color={cluster.color} />
            <span
              className="text-[10px] uppercase tracking-[0.18em]"
              style={{
                color: isEarned
                  ? '#10b981'
                  : isForming
                  ? '#f59e0b'
                  : '#64748b',
              }}
            >
              {cluster.status}
            </span>
          </div>
        </div>
        <div className="text-right">
          <div
            className="text-2xl font-light tabular-nums"
            style={{ color: isEarned ? `hsl(var(--ax-text))` : `hsl(var(--ax-text-muted))` }}
          >
            {pct}
            <span className="text-[10px] text-slate-600 ml-0.5">%</span>
          </div>
          <div className="text-[10px] text-slate-600">
            {cluster.completed_chains_14d}/{cluster.emits_14d} chains
          </div>
        </div>
      </div>
      <div className="mt-3 flex items-end justify-between">
        <StageBar stages={cluster.stage_distribution} />
        <div className="text-right space-y-0.5">
          <div className="text-[10px] text-slate-600 tabular-nums">
            {cluster.documentary_extensions_14d} doc episodes
          </div>
          {cluster.clip_velocity_median > 0 && (
            <div className="text-[10px] text-slate-700 tabular-nums">
              median velocity {cluster.clip_velocity_median}
            </div>
          )}
        </div>
      </div>
      {born && (
        <div
          className="mt-3 pt-3 text-[10px] tracking-wider italic"
          style={{ color: '#10b981', borderTop: '1px dashed rgba(16,185,129,0.18)' }}
        >
          Mythology born {born}
        </div>
      )}
    </div>
  );
}

export default function ArenaxMythologyOps() {
  const [theme, toggleTheme] = useArenaxTheme();
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let stopped = false;
    const load = async () => {
      try {
        const r = await api.get('/arenax/narrative-lifecycle/clusters');
        if (stopped) return;
        setData(r.data);
        setLoaded(true);
        setError(null);
      } catch (e) {
        if (e?.response?.status === 403) {
          setError('Super admin access required.');
        } else {
          setError('Cluster overview temporarily unavailable.');
        }
      }
    };
    load();
    const id = setInterval(load, POLL_MS);
    return () => {
      stopped = true;
      clearInterval(id);
    };
  }, []);

  return (
    <div
      className="min-h-screen arenax-themed relative"
      style={{
        background: 'var(--ax-mythology-bg)',
        color: `hsl(var(--ax-text))`,
      }}
      data-testid="arenax-mythology-ops-root"
    >
      <div className="absolute top-4 right-4 z-10">
        <ArenaxThemeToggle theme={theme} onToggle={toggleTheme} />
      </div>
      <div className="max-w-6xl mx-auto px-6 py-12 space-y-10">
        {/* Header — editorial, not analytical */}
        <div className="space-y-3">
          <div
            className="text-[10px] uppercase tracking-[0.32em]"
            style={{ color: `hsl(var(--ax-text-faint))` }}
          >
            ArenaX · Cultural Observability
          </div>
          <h1
            className="text-3xl font-light tracking-tight"
            style={{ color: `hsl(var(--ax-text))`, fontFamily: 'Georgia, "Times New Roman", serif' }}
            data-testid="arenax-mythology-title"
          >
            Where sports memory is forming.
          </h1>
          <p
            className="text-sm max-w-2xl leading-relaxed"
            style={{
              color: `hsl(var(--ax-text-muted))`,
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontStyle: 'italic',
            }}
          >
            Emerald clusters sustain memory beyond the match. Amber clusters are
            still gathering tension. Gray clusters have not yet begun to persist.
            Mythology is earned, never declared.
          </p>
        </div>

        {/* Totals strip — quiet count, not KPI */}
        {loaded && data?.totals && (
          <div
            className="flex flex-wrap items-center gap-x-8 gap-y-3"
            data-testid="arenax-mythology-totals"
          >
            <div className="flex items-center gap-2">
              <StatusDot color="emerald" />
              <span
                className="text-[11px] tabular-nums"
                style={{ color: `hsl(var(--ax-text))` }}
              >
                {data.totals.earned} earned
              </span>
            </div>
            <div className="flex items-center gap-2">
              <StatusDot color="amber" />
              <span
                className="text-[11px] tabular-nums"
                style={{ color: `hsl(var(--ax-text-muted))` }}
              >
                {data.totals.amber} forming
              </span>
            </div>
            <div className="flex items-center gap-2">
              <StatusDot color="gray" />
              <span
                className="text-[11px] tabular-nums"
                style={{ color: `hsl(var(--ax-text-faint))` }}
              >
                {data.totals.gray} emerging
              </span>
            </div>
            <span
              className="ml-auto text-[10px] tracking-wider"
              style={{ color: `hsl(var(--ax-text-faint))` }}
            >
              {data.totals.clusters} clusters tracked
            </span>
          </div>
        )}

        {/* Clusters grid — patient incubation language for empty state */}
        {!loaded && !error && (
          <div
            className="py-16 text-center text-[11px] uppercase tracking-[0.25em] arenax-mythology-breath"
            style={{ color: `hsl(var(--ax-text-faint))` }}
          >
            Reading the cities…
          </div>
        )}
        {error && (
          <div
            className="py-12 text-[12px] italic"
            style={{ color: 'hsl(0 70% 55%)' }}
            data-testid="arenax-mythology-error"
          >
            {error}
          </div>
        )}
        {loaded && data?.clusters?.length === 0 && (
          <div
            className="py-20 text-center arenax-mythology-quiet-empty"
            data-testid="arenax-mythology-quiet"
          >
            <div className="flex justify-center mb-4">
              <span className="arenax-mythology-breath-dot" />
            </div>
            <p
              className="text-[13px] italic max-w-md mx-auto leading-relaxed"
              style={{
                color: `hsl(var(--ax-text-muted))`,
                fontFamily: 'Georgia, "Times New Roman", serif',
              }}
            >
              No mythology forming yet.<br />
              The cities are still arriving.
            </p>
          </div>
        )}
        {loaded && data?.clusters?.length > 0 && (
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3"
            data-testid="arenax-mythology-grid"
          >
            {data.clusters.map(c => (
              <ClusterTile key={`${c.city}__${c.sport_norm}`} cluster={c} />
            ))}
          </div>
        )}

        <div
          className="pt-10 text-[10px] tracking-wider"
          style={{ color: `hsl(var(--ax-text-faint))` }}
        >
          ArenaX · cultural observability layer · v2R-mythology
        </div>
      </div>
    </div>
  );
}
